/**
 * Workflow Step Registry
 *
 * Data-driven definitions for workflow steps, substeps, validation,
 * and contextual help content.
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import type {
  WorkflowStepId,
  WorkflowStepConfig,
  WorkflowSubstep,
  WorkflowField,
  ConfidenceLevel,
} from '@/types/workflow';

// ============================================================================
// STEP DEFINITIONS
// ============================================================================

/**
 * Extended step configuration with validation and help content
 */
export interface ExtendedWorkflowStep extends WorkflowStepConfig {
  validation?: StepValidation;
  helpContent: StepHelpContent;
  requiredFields?: string[];
  requiredDocuments?: string[];
}

/**
 * Validation rules for a step
 */
export interface StepValidation {
  requiredFields: string[];
  customValidators?: StepValidator[];
}

export interface StepValidator {
  name: string;
  validate: (jobData: Record<string, unknown>) => ValidationResult;
}

export interface ValidationResult {
  isValid: boolean;
  message?: string;
  severity?: 'error' | 'warning';
}

/**
 * Help content for a step
 */
export interface StepHelpContent {
  title: string;
  description: string;
  tips?: string[];
  commonMistakes?: string[];
  relatedTopics?: { label: string; link?: string }[];
}

// ============================================================================
// STEP REGISTRY
// ============================================================================

export const WORKFLOW_STEP_REGISTRY: Record<WorkflowStepId, ExtendedWorkflowStep> = {
  basics: {
    id: 'basics',
    number: 1,
    title: 'Basics & Job Snapshot',
    shortTitle: 'Basics',
    description: 'Confirm job identity & jurisdiction context',
    substeps: [
      { id: 'address', title: 'Address confirmation', required: true, completionField: 'address' },
      { id: 'jurisdiction', title: 'Jurisdiction confirmation', required: true, completionField: 'jurisdictionId' },
      { id: 'propertyType', title: 'Property type', required: true, completionField: 'propertyType' },
      { id: 'valuation', title: 'Project valuation', required: false, completionField: 'estimatedValue' },
      { id: 'contractorRole', title: 'Contractor role', required: true, completionField: 'contractorRole' },
    ],
    validation: {
      requiredFields: ['address', 'jurisdictionId', 'propertyType'],
    },
    helpContent: {
      title: 'Why we need this',
      description: 'The job address determines which jurisdiction reviews your permit application. Different jurisdictions have different requirements, forms, and fees.',
      tips: [
        'Double-check the address format matches how it appears on official documents',
        'If the property is in an unincorporated area, it typically falls under county jurisdiction',
        'Some addresses span multiple jurisdictions - we\'ll help you pick the right one',
      ],
      commonMistakes: [
        'Using a mailing address instead of the physical property address',
        'Selecting the wrong county for border properties',
        'Forgetting unit/suite numbers for multi-unit buildings',
      ],
    },
    requiredFields: ['location', 'jurisdictionId'],
  },

  scope: {
    id: 'scope',
    number: 2,
    title: 'Scope & Permit Definition',
    shortTitle: 'Scope',
    description: 'Confirm scope + permit type recommendation',
    substeps: [
      { id: 'trade', title: 'Trade confirmation', required: true, completionField: 'permitType' },
      { id: 'jobScope', title: 'Job scope selection', required: true, completionField: 'jobScopeId' },
      { id: 'permitType', title: 'Permit type confirmation', required: true, completionField: 'permitTypeId' },
      { id: 'override', title: 'Override handling', required: false },
    ],
    validation: {
      requiredFields: ['permitType', 'jobScope'],
    },
    helpContent: {
      title: 'Choosing the right permit type',
      description: 'Selecting the correct permit type ensures your application routes to the right review team and includes the correct requirements.',
      tips: [
        'Choose the scope that most closely matches your project',
        'When in doubt, choose the more comprehensive permit type',
        'Our system auto-recommends based on typical patterns',
      ],
      commonMistakes: [
        'Selecting a "repair" permit when "replacement" is required',
        'Underestimating scope to get a simpler permit',
        'Missing auxiliary permits (e.g., needing both electrical and building)',
      ],
      relatedTopics: [
        { label: 'Permit Types Guide' },
        { label: 'When do I need a Building permit?' },
      ],
    },
    requiredFields: ['permitType', 'jobScope'],
  },

  fields: {
    id: 'fields',
    number: 3,
    title: 'Required Fields',
    shortTitle: 'Data',
    description: 'Complete permit application data',
    substeps: [
      { id: 'applicant', title: 'Applicant info', required: true },
      { id: 'propertyOwner', title: 'Property owner', required: false },
      { id: 'projectDescription', title: 'Project description', required: true },
      { id: 'tradeSpecific', title: 'Trade-specific fields', required: true },
      { id: 'unknownHandling', title: '"I don\'t know" handling', required: false },
    ],
    validation: {
      requiredFields: ['applicantName', 'projectDescription'],
    },
    helpContent: {
      title: 'Application data requirements',
      description: 'These fields will be used to pre-fill your permit application forms. The more accurate the data, the faster your permit gets approved.',
      tips: [
        'Use your license name exactly as it appears on your license',
        'Property owner info is required for Notice of Commencement',
        'Trade-specific fields (like load calculations) may require engineer input',
      ],
      commonMistakes: [
        'Using a nickname instead of legal name',
        'Incorrect license number format',
        'Missing property owner contact info',
      ],
    },
    requiredFields: ['applicantName', 'projectDescription'],
  },

  documents: {
    id: 'documents',
    number: 4,
    title: 'Documents / Packet Builder',
    shortTitle: 'Docs',
    description: 'Upload required documents and verify them',
    substeps: [
      { id: 'requiredDocs', title: 'Required documents list', required: true },
      { id: 'perDocument', title: 'Per-document card', required: true },
      { id: 'verification', title: 'Document verification', required: false },
      { id: 'missingDocs', title: 'Missing documents action', required: false },
    ],
    validation: {
      requiredFields: [],
    },
    helpContent: {
      title: 'Building your permit packet',
      description: 'A complete document packet is crucial for permit approval. We show you exactly what\'s required based on your jurisdiction and scope.',
      tips: [
        'Upload documents as PDFs when possible',
        'Ensure drawings are to scale and include all required details',
        'Some jurisdictions require wet signatures - check requirements',
      ],
      commonMistakes: [
        'Uploading expired insurance certificates',
        'Submitting unsigned drawings',
        'Missing page numbers on multi-page documents',
        'Low resolution scans that are hard to read',
      ],
      relatedTopics: [
        { label: 'Document Requirements by Trade' },
        { label: 'How to prepare sealed drawings' },
      ],
    },
    requiredDocuments: ['permit_application', 'proof_of_insurance'],
  },

  compliance: {
    id: 'compliance',
    number: 5,
    title: 'Compliance Preflight',
    shortTitle: 'Comply',
    description: 'Run checks & resolve blockers',
    substeps: [
      { id: 'documentChecks', title: 'Document checks', required: true },
      { id: 'fieldChecks', title: 'Field checks', required: true },
      { id: 'jurisdictionChecks', title: 'Jurisdiction requirements', required: true },
      { id: 'licenseChecks', title: 'License verification', required: true },
      { id: 'tradeChecks', title: 'Trade-specific requirements', required: true },
    ],
    validation: {
      requiredFields: [],
      customValidators: [
        {
          name: 'noBlockers',
          validate: (data) => ({
            isValid: !data.hasBlockers,
            message: data.hasBlockers ? 'Resolve all blockers before continuing' : undefined,
            severity: 'error',
          }),
        },
      ],
    },
    helpContent: {
      title: 'Pre-submission compliance check',
      description: 'We automatically check your application against jurisdiction requirements, license scope, and document requirements.',
      tips: [
        'Fix all "blockers" before proceeding - these will cause rejection',
        '"Warnings" can be submitted but may slow down review',
        'Re-run the check after making changes',
      ],
      commonMistakes: [
        'Ignoring HVHZ (High Velocity Hurricane Zone) requirements',
        'Submitting without NOC when required',
        'Missing fire sprinkler coordination',
      ],
    },
  },

  inspections: {
    id: 'inspections',
    number: 6,
    title: 'Inspections & Checkpoints',
    shortTitle: 'Inspect',
    description: 'Configure inspection requirements',
    substeps: [
      { id: 'requiredInspections', title: 'Required inspections', required: true },
      { id: 'checkpoints', title: 'Checkpoint checklist', required: false },
      { id: 'knownTips', title: 'Known tips', required: false },
      { id: 'acknowledgment', title: 'Acknowledgment', required: false },
    ],
    validation: {
      requiredFields: [],
    },
    helpContent: {
      title: 'Inspection preparation',
      description: 'Understanding inspection requirements before you start work helps avoid delays and failed inspections.',
      tips: [
        'Review the inspection sequence before starting work',
        'Each inspection must pass before the next phase begins',
        'Have permit and approved plans on-site during inspections',
      ],
      commonMistakes: [
        'Covering work before inspection (e.g., drywall over electrical)',
        'Not having permit card posted visibly',
        'Missing required labels/stickers on equipment',
      ],
      relatedTopics: [
        { label: 'Common Inspection Failures' },
        { label: 'How to schedule inspections' },
      ],
    },
  },

  review: {
    id: 'review',
    number: 7,
    title: 'Review & Submit',
    shortTitle: 'Review',
    description: 'Final review & submission readiness',
    substeps: [
      { id: 'summary', title: 'Job summary', required: true },
      { id: 'checklist', title: 'Completion checklist', required: true },
      { id: 'warnings', title: 'Remaining warnings', required: false },
      { id: 'packetPreview', title: 'Packet preview', required: true },
    ],
    validation: {
      requiredFields: [],
      customValidators: [
        {
          name: 'readyForSubmission',
          validate: (data) => ({
            isValid: data.dataComplete && data.packComplete && !data.hasBlockers,
            message: 'Complete all requirements before submission',
            severity: 'error',
          }),
        },
      ],
    },
    helpContent: {
      title: 'Final review',
      description: 'Review your complete application before submission. This is your last chance to catch any issues.',
      tips: [
        'Double-check all names and numbers',
        'Verify document page counts match your records',
        'Save a copy of your packet for your records',
      ],
      commonMistakes: [
        'Forgetting to sign application forms',
        'Not keeping copies of submitted documents',
        'Submitting during office closure hours (delays processing)',
      ],
      relatedTopics: [
        { label: 'Jurisdiction submission portals' },
        { label: 'What to expect after submission' },
      ],
    },
  },
};

// ============================================================================
// HELP CONTENT BY SUBSTEP
// ============================================================================

export interface SubstepHelpContent {
  title: string;
  description: string;
  fieldHelp?: Record<string, string>;
}

export const SUBSTEP_HELP: Record<string, SubstepHelpContent> = {
  // Basics substeps
  'basics.address': {
    title: 'Job Address',
    description: 'Enter the physical location where the permitted work will be performed.',
    fieldHelp: {
      street: 'Include house number and street name',
      city: 'The city or unincorporated area name',
      zip: '5-digit ZIP code',
    },
  },
  'basics.jurisdiction': {
    title: 'Jurisdiction Lookup',
    description: 'We automatically determine which permitting authority has jurisdiction over this address.',
  },
  'basics.propertyType': {
    title: 'Property Type',
    description: 'Residential and commercial properties have different permit requirements and review processes.',
  },
  'basics.valuation': {
    title: 'Project Valuation',
    description: 'The estimated total project value affects permit fees and may trigger additional requirements (e.g., Notice of Commencement).',
  },
  'basics.contractorRole': {
    title: 'Your Role',
    description: 'Your role determines which permits you\'ll pull and how inspections are coordinated.',
  },

  // Scope substeps
  'scope.trade': {
    title: 'Trade Selection',
    description: 'Select the primary trade for this permit. Multi-trade projects may require separate permits.',
  },
  'scope.jobScope': {
    title: 'Job Scope',
    description: 'The specific type of work being performed. This determines document requirements.',
  },
  'scope.permitType': {
    title: 'Permit Type',
    description: 'Based on your scope, we recommend the appropriate permit type. You can override if needed.',
  },

  // Fields substeps
  'fields.applicant': {
    title: 'Applicant Information',
    description: 'The licensed contractor or owner-builder pulling the permit.',
    fieldHelp: {
      name: 'Name as it appears on your license',
      licenseNumber: 'Your active state license number',
      phone: 'Contact phone for permit-related calls',
    },
  },
  'fields.propertyOwner': {
    title: 'Property Owner',
    description: 'Required for Notice of Commencement and owner authorization.',
  },
  'fields.projectDescription': {
    title: 'Project Description',
    description: 'A clear description of the work to be performed.',
  },

  // Documents substeps
  'documents.requiredDocs': {
    title: 'Required Documents',
    description: 'These documents are required for permit submission in your jurisdiction.',
  },

  // Compliance substeps
  'compliance.documentChecks': {
    title: 'Document Checks',
    description: 'Verifying all required documents are uploaded and valid.',
  },
  'compliance.jurisdictionChecks': {
    title: 'Jurisdiction Requirements',
    description: 'Checking jurisdiction-specific requirements like HVHZ, flood zones, and NOC thresholds.',
  },

  // Review substeps
  'review.summary': {
    title: 'Application Summary',
    description: 'Review your complete application details before submission.',
  },
  'review.packetPreview': {
    title: 'Document Packet',
    description: 'All documents that will be included in your permit submission.',
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get extended step configuration
 */
export function getExtendedStep(stepId: WorkflowStepId): ExtendedWorkflowStep {
  return WORKFLOW_STEP_REGISTRY[stepId];
}

/**
 * Get help content for a step
 */
export function getStepHelp(stepId: WorkflowStepId): StepHelpContent {
  return WORKFLOW_STEP_REGISTRY[stepId].helpContent;
}

/**
 * Get help content for a substep
 */
export function getSubstepHelp(stepId: WorkflowStepId, substepId: string): SubstepHelpContent | undefined {
  return SUBSTEP_HELP[`${stepId}.${substepId}`];
}

/**
 * Validate a step
 */
export function validateStep(
  stepId: WorkflowStepId,
  jobData: Record<string, unknown>
): ValidationResult[] {
  const step = WORKFLOW_STEP_REGISTRY[stepId];
  const results: ValidationResult[] = [];

  // Check required fields
  if (step.validation?.requiredFields) {
    for (const field of step.validation.requiredFields) {
      if (!jobData[field]) {
        results.push({
          isValid: false,
          message: `${field} is required`,
          severity: 'error',
        });
      }
    }
  }

  // Run custom validators
  if (step.validation?.customValidators) {
    for (const validator of step.validation.customValidators) {
      results.push(validator.validate(jobData));
    }
  }

  return results;
}

/**
 * Get all steps in order
 */
export function getAllSteps(): ExtendedWorkflowStep[] {
  return Object.values(WORKFLOW_STEP_REGISTRY).sort((a, b) => a.number - b.number);
}

/**
 * Calculate overall progress across all steps
 */
export function calculateOverallProgress(
  completedSubsteps: Record<WorkflowStepId, string[]>
): number {
  let total = 0;
  let completed = 0;

  for (const step of getAllSteps()) {
    const requiredSubsteps = step.substeps.filter((s) => s.required);
    total += requiredSubsteps.length;
    completed += requiredSubsteps.filter(
      (s) => completedSubsteps[step.id]?.includes(s.id)
    ).length;
  }

  return total > 0 ? Math.round((completed / total) * 100) : 0;
}
